/**
 * iter75.2 — Company Overhead ledger (Control Room).
 *
 * The company's own operating cost ledger — lease, insurance, social
 * contributions, accounting fees. Never charged back to drivers,
 * lives entirely in-app (independent of the Google Sheet payouts).
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Plus, Trash2, X } from "lucide-react";
import { isoWeekOf } from "../common";

const OVERHEAD_TYPES = [
  "LEASE", "SOCIAL_FEE", "INSURANCE", "FLEET_FUEL",
  "FLEET_MAINT", "PLATFORM_FEE", "ACCOUNTING", "OTHER",
];

const eur = (n) => `€${Number(n || 0).toFixed(2)}`;

export function CompanyOverheadTab() {
  const [rows, setRows] = useState([]);
  const [week, setWeek] = useState(isoWeekOf(new Date()));
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/control-room/overhead", {
        params: { iso_week: week },
      });
      setRows(Array.isArray(data) ? data : (data?.rows || []));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load overhead.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [week]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this overhead entry?")) return;
    try {
      await api.delete(`/admin/control-room/overhead/${id}`);
      toast.success("Deleted.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  const total = rows.reduce((sum, r) => sum + Number(r.amount_eur || 0), 0);

  return (
    <div data-testid="overhead-tab">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <label className="text-xs text-zinc-500">ISO Week</label>
        <input
          value={week}
          onChange={(e) => setWeek(e.target.value)}
          placeholder="YYYY-Www"
          className="bg-black/40 border border-white/10 px-3 py-1.5 rounded-lg text-sm w-32 focus:outline-none focus:border-[#C5A059]/60"
          data-testid="overhead-week-input"
        />
        <div className="ml-auto flex items-center gap-4">
          <div className="text-xs">
            <span className="text-zinc-500 uppercase tracking-widest">Total</span>{" "}
            <span className="text-[#C5A059] font-semibold">{eur(total)}</span>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#C5A059]/40 text-[#C5A059] text-sm hover:bg-[#C5A059]/10 transition"
            data-testid="overhead-add-btn"
          >
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.25em] text-zinc-500 border-b border-white/5">
              <th className="px-3 py-3">Date</th>
              <th className="px-3 py-3">Type</th>
              <th className="px-3 py-3">Vendor</th>
              <th className="px-3 py-3">Vehicle</th>
              <th className="px-3 py-3">Notes</th>
              <th className="px-3 py-3 text-right">Amount</th>
              <th className="px-3 py-3 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="py-10 text-center text-zinc-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="py-10 text-center text-zinc-500">
                No overhead entries for {week}. Click <span className="text-[#C5A059]">Add</span> to create one.
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-white/5" data-testid={`overhead-row-${r.id}`}>
                <td className="px-3 py-3 text-zinc-300 whitespace-nowrap">{r.incurred_on || r.iso_week}</td>
                <td className="px-3 py-3 text-zinc-200">
                  <span className="text-[10px] uppercase tracking-widest border border-white/10 px-2 py-0.5 rounded-full">
                    {r.type}
                  </span>
                </td>
                <td className="px-3 py-3 text-zinc-300">{r.vendor || "—"}</td>
                <td className="px-3 py-3 text-zinc-300">{r.vehicle_label || "—"}</td>
                <td className="px-3 py-3 text-zinc-500 text-xs truncate max-w-[280px]">{r.notes || ""}</td>
                <td className="px-3 py-3 text-right text-zinc-200">{eur(r.amount_eur)}</td>
                <td className="px-3 py-3">
                  <button
                    onClick={() => del(r.id)}
                    className="p-1.5 rounded-full border border-white/10 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 transition"
                    title="Delete"
                    data-testid={`overhead-delete-${r.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && <OverheadModal week={week} onClose={() => setShowAdd(false)} onSaved={load} />}
    </div>
  );
}

function OverheadModal({ week, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    type: "LEASE",
    amount_eur: "",
    vendor: "",
    vehicle_label: "",
    notes: "",
    iso_week: week,
    incurred_on: today,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.amount_eur || Number(form.amount_eur) <= 0) {
      toast.error("Amount is required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/control-room/overhead", {
        ...form,
        amount_eur: Number(form.amount_eur),
      });
      toast.success("Overhead saved.");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="max-w-lg w-full bg-[#0a0a0a] border border-white/10 rounded-2xl p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="overhead-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-light">New Overhead Entry</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <Field label="Type">
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="w-full bg-black/60 border border-white/10 px-3 py-2 rounded-lg text-sm"
              data-testid="overhead-type"
            >
              {OVERHEAD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount (EUR)">
              <input
                type="number" step="0.01" min="0"
                value={form.amount_eur}
                onChange={(e) => setForm({ ...form, amount_eur: e.target.value })}
                className="w-full bg-black/60 border border-white/10 px-3 py-2 rounded-lg text-sm"
                data-testid="overhead-amount"
              />
            </Field>
            <Field label="Date">
              <input
                type="date"
                value={form.incurred_on}
                onChange={(e) => setForm({ ...form, incurred_on: e.target.value })}
                className="w-full bg-black/60 border border-white/10 px-3 py-2 rounded-lg text-sm"
              />
            </Field>
          </div>
          <Field label="Vendor">
            <input
              value={form.vendor}
              onChange={(e) => setForm({ ...form, vendor: e.target.value })}
              className="w-full bg-black/60 border border-white/10 px-3 py-2 rounded-lg text-sm"
              placeholder="e.g. INA, Croatia osiguranje"
            />
          </Field>
          <Field label="Vehicle / label (optional)">
            <input
              value={form.vehicle_label}
              onChange={(e) => setForm({ ...form, vehicle_label: e.target.value })}
              className="w-full bg-black/60 border border-white/10 px-3 py-2 rounded-lg text-sm"
              placeholder="ST-1234-AB, or blank for company-level"
            />
          </Field>
          <Field label="Notes">
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full bg-black/60 border border-white/10 px-3 py-2 rounded-lg text-sm"
            />
          </Field>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-full border border-white/10 text-zinc-400 text-sm">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 rounded-full bg-[#C5A059] text-black text-sm font-semibold disabled:opacity-40"
            data-testid="overhead-save"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
